import { Megaphone, Calendar, ChatCircleText, UserFocus, PaperPlaneTilt, ThumbsUp, ThumbsDown, Trophy, Warning, Clock } from 'phosphor-react';
import { useEffect, useState } from 'react';
import { hrDashboardService } from '../services/hrDashboardService';
import { useToast } from '../components/Toast';
import { Complaint, ComplaintLeaderboardEntry } from '../types';

const statusBadge = (status: Complaint['status']) => {
  switch (status) {
    case 'VALID':
      return <span className="inline-flex items-center gap-1 rounded-full border border-red-400/30 bg-red-500/10 px-3 py-1 text-xs font-medium text-red-200">Valid &middot; strike given</span>;
    case 'INVALID':
      return <span className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium text-white/50">Dismissed</span>;
    default:
      return <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/30 bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-200">Voting open</span>;
  }
};

const toDatetimeLocal = (date: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const ComplaintsPage = () => {
  const { showToast } = useToast();

  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [leaderboard, setLeaderboard] = useState<ComplaintLeaderboardEntry[]>([]);
  const [knownNames, setKnownNames] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [incidentAt, setIncidentAt] = useState(() => toDatetimeLocal(new Date()));
  const [explanation, setExplanation] = useState('');
  const [aboutName, setAboutName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [complaintList, leaderboardList, names] = await Promise.all([
        hrDashboardService.getComplaints(),
        hrDashboardService.getComplaintLeaderboard(),
        hrDashboardService.getComplaintNames(),
      ]);
      setComplaints(complaintList);
      setLeaderboard(leaderboardList);
      setKnownNames(names);
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to load complaints', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSubmit = async () => {
    if (aboutName.trim().length === 0) {
      showToast('Fill in who the complaint is about', 'error');
      return;
    }
    if (explanation.trim().length < 3) {
      showToast('Add a short explanation', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      await hrDashboardService.createComplaint({
        aboutName: aboutName.trim(),
        incidentAt: new Date(incidentAt).toISOString(),
        explanation: explanation.trim(),
      });
      showToast('Complaint filed and sent to Slack for voting');
      setExplanation('');
      setAboutName('');
      setIncidentAt(toDatetimeLocal(new Date()));
      loadData();
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to file complaint', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative isolate space-y-6">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/[0.12] to-white/[0.04] p-6 text-white shadow-glow backdrop-blur-2xl sm:p-8">
        <div className="pointer-events-none absolute -right-10 -top-10 h-48 w-48 rounded-full bg-brand-500/20 blur-3xl" />
        <div className="relative max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium uppercase tracking-[0.25em] text-brand-200/90">
            <Megaphone size={14} weight="fill" /> Complaints
          </div>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">File a complaint</h1>
          <p className="mt-3 max-w-xl text-sm leading-6 text-white/70 sm:text-base">
            Purely for office fun. File it, the team votes 👍 / 👎 on Slack, and a net of 3 votes decides whether a strike lands.
          </p>
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.4fr)_340px]">
        {/* Form */}
        <section className="rounded-[2rem] border border-white/10 bg-white/[0.07] p-6 text-white shadow-glow backdrop-blur-2xl sm:p-8">
          <div className="border-b border-white/10 pb-6">
            <div className="text-sm uppercase tracking-[0.3em] text-brand-200/90">New complaint</div>
            <h2 className="mt-2 text-2xl font-semibold text-white">Details</h2>
          </div>

          <div className="mt-6 space-y-6">
            <div>
              <label className="mb-2 block text-sm font-medium text-white/85">About</label>
              <div className="relative">
                <UserFocus size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/35" />
                <input
                  type="text"
                  value={aboutName}
                  onChange={(event) => setAboutName(event.target.value)}
                  list="complaint-about-names"
                  placeholder="Type a name..."
                  className="w-full rounded-[1.25rem] border border-white/10 bg-black/30 py-3 pl-11 pr-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-brand-400/60 focus:ring-2 focus:ring-brand-400/20"
                />
                <datalist id="complaint-about-names">
                  {knownNames.map((name) => (
                    <option key={name} value={name} />
                  ))}
                </datalist>
              </div>
              <p className="mt-2 text-xs text-white/45">Any name works &mdash; previously filed names show up as suggestions.</p>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-white/85">Date & time</label>
              <div className="relative">
                <Calendar size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/35" />
                <input
                  type="datetime-local"
                  value={incidentAt}
                  onChange={(event) => setIncidentAt(event.target.value)}
                  className="w-full rounded-[1.25rem] border border-white/10 bg-black/30 py-3 pl-11 pr-4 text-sm text-white outline-none transition focus:border-brand-400/60 focus:ring-2 focus:ring-brand-400/20"
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-white/85">Explanation</label>
              <div className="relative">
                <ChatCircleText size={18} className="pointer-events-none absolute left-4 top-4 text-white/35" />
                <textarea
                  value={explanation}
                  onChange={(event) => setExplanation(event.target.value)}
                  rows={5}
                  className="w-full rounded-[1.25rem] border border-white/10 bg-black/30 py-3 pl-11 pr-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-brand-400/60 focus:ring-2 focus:ring-brand-400/20"
                  placeholder="What happened?"
                />
              </div>
            </div>

            <div className="flex flex-col gap-3 border-t border-white/10 pt-6 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-xs text-white/50">Posted to Slack for the team to vote on.</div>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-brand-500 px-6 py-3 text-sm font-semibold text-white shadow-glow transition hover:bg-brand-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <PaperPlaneTilt size={18} /> {isSubmitting ? 'Filing...' : 'File complaint'}
              </button>
            </div>
          </div>
        </section>

        {/* Leaderboard */}
        <aside className="space-y-4">
          <section className="rounded-[2rem] border border-white/10 bg-white/[0.07] p-6 text-white shadow-glow backdrop-blur-2xl">
            <div className="flex items-center gap-2 text-sm uppercase tracking-[0.3em] text-brand-200/90">
              <Trophy size={18} weight="fill" /> Strike leaderboard
            </div>
            <p className="mt-2 text-xs text-white/45">One strike per complaint the team votes as valid.</p>

            <div className="mt-4 space-y-2">
              {leaderboard.length === 0 ? (
                <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-6 text-center text-sm text-white/45">
                  No strikes yet. A clean slate&hellip; for now.
                </div>
              ) : (
                leaderboard.map((entry, index) => (
                  <div
                    key={entry.name}
                    className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-black/20 px-4 py-3"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-red-400/30 to-red-600/20 text-xs font-semibold text-red-100 ring-1 ring-red-400/20">
                        #{index + 1}
                      </span>
                      <span className="truncate text-sm font-medium text-white">{entry.name}</span>
                    </div>
                    <span className="inline-flex items-center gap-1 rounded-full border border-red-400/30 bg-red-500/10 px-3 py-1 text-xs font-semibold text-red-200">
                      <Warning size={14} weight="fill" /> {entry.strikes}
                    </span>
                  </div>
                ))
              )}
            </div>
          </section>

          <section className="rounded-[2rem] border border-white/10 bg-white/[0.07] p-6 text-white shadow-glow backdrop-blur-2xl">
            <div className="text-xs uppercase tracking-[0.25em] text-white/45">How it works</div>
            <p className="mt-2 text-sm leading-6 text-white/70">
              Every complaint posts to Slack with 👍 / 👎 reactions. A net of 3 votes either way resolves it &mdash; valid means a strike, invalid means it&apos;s dismissed.
            </p>
          </section>
        </aside>
      </div>

      {/* Complaint history */}
      <section className="rounded-[2rem] border border-white/10 bg-white/[0.07] p-6 text-white shadow-glow backdrop-blur-2xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm uppercase tracking-[0.3em] text-brand-200/90">
            <Clock size={18} weight="fill" /> Recent complaints
          </div>
          <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/60">
            {complaints.length} {complaints.length === 1 ? 'complaint' : 'complaints'}
          </div>
        </div>

        <div className="mt-4 space-y-3">
          {isLoading ? (
            <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-10 text-center text-sm text-white/45">Loading complaints...</div>
          ) : complaints.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-2xl border border-white/10 bg-black/20 px-4 py-12 text-center text-sm text-white/45">
              <Megaphone size={28} className="text-white/25" />
              No complaints filed yet.
            </div>
          ) : (
            complaints.map((complaint) => (
              <div key={complaint.id} className="rounded-2xl border border-white/10 bg-black/20 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm font-medium text-white">
                    {complaint.aboutName} <span className="text-white/40">&middot; filed by {complaint.filedByName}</span>
                  </div>
                  {statusBadge(complaint.status)}
                </div>
                <div className="mt-1 text-xs text-white/45">{new Date(complaint.incidentAt).toLocaleString()}</div>
                <p className="mt-2 text-sm text-white/75">{complaint.explanation}</p>
                <div className="mt-3 flex items-center gap-4 text-xs text-white/50">
                  <span className="inline-flex items-center gap-1"><ThumbsUp size={14} /> {complaint.upVotes}</span>
                  <span className="inline-flex items-center gap-1"><ThumbsDown size={14} /> {complaint.downVotes}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
};

export default ComplaintsPage;
